import invariant from 'invariant'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {parseLinkHeader} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {fancyClient, extractDataAndHeaders} from '@freckle/educator-entities/ts/openapi-clients'
import type {RosterSourceT} from '@freckle/educator-entities/ts/users/logic/roster-source'
import {providersOrNoneToQueryFilter} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {StudentTableRowT} from '@freckle/school/ts/manage-teachers/models/student-table-row'
import {parseStudentTableRows} from '@freckle/school/ts/manage-teachers/models/student-table-row'
import type {StudentTabFilteringT} from './../components/student-tab/student-tab'

export function fetchStudentTableRows(
  schoolIds: Array<number>,
  tableParams: TableParamsT<StudentTabFilteringT>
): Promise<{
  studentTableRows: Array<StudentTableRowT>
  links: LinksT
  totalCount: number
}> {
  const sourcesArray = {
    admin: ['upload', 'idp_sync'],
    'non-admin': ['manual', 'none']
  }

  const {additionalFilters} = tableParams

  const identityProviderFilterParam = providersOrNoneToQueryFilter(
    additionalFilters?.identityProviders ?? []
  )

  const query = {
    'schools.id': schoolIds,
    position: tableParams.position ?? undefined,
    limit: tableParams.limit ?? undefined,
    name: additionalFilters?.studentName ?? undefined,
    'courses.id': additionalFilters?.course?.id,
    'teachers.id': additionalFilters?.teacher?.id,
    'teachers.name': additionalFilters?.teacherName ?? undefined,
    grade: additionalFilters?.grade ?? undefined,
    source: additionalFilters?.source
      ? (sourcesArray[additionalFilters.source][0] as RosterSourceT)
      : undefined,
    'sis-id':
      additionalFilters?.sisId !== null && additionalFilters?.sisId !== undefined
        ? additionalFilters.sisId.toString()
        : undefined,
    ...identityProviderFilterParam
  }

  return fancyClient
    .GET('/3/students', {params: {query}})
    .then(extractDataAndHeaders)
    .then(({data, headers}) => {
      invariant(Array.isArray(data), 'We should always be receiving the json response, not the csv')
      const linkHeader = headers.get('Link')
      const links = parseLinkHeader(linkHeader)

      const totalCountString = headers.get('X-Total-Count')
      const totalCount = totalCountString !== null ? parseInt(totalCountString, 10) : 0

      const studentTableRows = parseStudentTableRows(data)
      return {studentTableRows, links, totalCount}
    })
}
